// CORE
import React from 'react';

import { accountsUtils } from 'app-components/flux/accounts';
import { billerAccountsSelectors } from 'app-components/flux/biller-accounts';
import { billerLoginsSelectors, billerLoginsUtils } from 'app-components/flux/biller-logins';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import DownloadingIcon from '@mui/icons-material/DownloadingRounded';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassIcon from '@mui/icons-material/HourglassBottomRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import UpdatedIcon from '@mui/icons-material/VerifiedRounded';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';

// DATA
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles<{ size: number; error: boolean; isPending: boolean; open: boolean; updated: boolean }>()(
  (theme, { size, error, isPending, open, updated }) => ({
    icon: {
      height: size,
      width: size,
      bottom: -size / 4,
      right: -size / 2 + size / 8,
      position: 'absolute',
      borderRadius: size / 2,

      zIndex: 1,
      backgroundColor: open && !error ? theme.palette.pending.main : theme.palette.background.paper,
      color: error
        ? theme.palette.error.main
        : updated
          ? theme.palette.success.main
          : open
            ? theme.palette.common.white
            : theme.palette.primary.main,
      padding: error ? (size - 16) / 8 : isPending ? size / 6 : size / 16,
      boxShadow: theme.shadows[13],
    },
  }),
);

interface ConnectedIconProps {
  model?: scheduledTransactionsTypes.ScheduledTransactionType;
  txn?: TransactionProps;
  size?: number;
  hasBVA?: boolean;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ConnectedIcon(props: ConnectedIconProps) {
  const { model, size = 24, txn, hasBVA } = props;

  let showIcon = false;
  let updated = false;
  let isPending = false;
  let open = false;
  let error = false;
  let title = '';
  let Icon: any = LinkIcon;

  // fiServ Presentment stuff
  const fiServ = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'billsFiserv'));

  const BPSpending = model?.billPresentmentAccountId === '0';
  const BPSconnected = model?.billPresentmentAccountId && !BPSpending;

  const billerAccount = useAppSelector(
    (state) => BPSconnected && billerAccountsSelectors.getBillerAccountForId(state, model?.billPresentmentAccountId),
  );
  const billerLogin = useAppSelector(
    (state) => BPSconnected && billerLoginsSelectors.getBillerLoginForId(state, billerAccount?.billerLoginID),
  );

  const displayTxn = txn || model?.transaction || ({} as TransactionProps);
  const creditAccount = useAppSelector((state) => transactionsSelectors.getCreditAccountFromTxn(state, displayTxn));

  if (hasBVA) {
    // BVA stuff
    showIcon = true;
    title = 'Connected';
    // eslint-disable-next-line
    if (accountsUtils.isAccountInError(creditAccount)?.isError) {
      title = 'Connection Error';
      Icon = ErrorIcon;
      error = true;
    } else if (txn?.ebillId) {
      if (
        txn.ebillId === '1' || // Bank/BVA statement amount
        txn.ebillId === '2' // QCS sum after statement close
      ) {
        title = 'Your bill amount has been finalized.';
        Icon = UpdatedIcon;
        updated = true;
      } else if (txn.ebillId === '3') {
        // QCS sum while statement open
        title = 'Statement period has begun and amount will continue to update.';
        open = true;
        Icon = DownloadingIcon;
      }
    }
  } else if (model?.billPresentmentAccountId || (fiServ && model?.presentmentAccountId)) {
    if (fiServ && model?.presentmentAccountId) {
      // fiServ presentment
      showIcon = true;
      title = 'Connected. Bill will be available soon';
      switch (model.billerEnrollmentStatus) {
        case 'PENDING':
          isPending = true;
          title = 'Connecting. This may take a day or two';
          Icon = HourglassIcon;
          break;
        case 'DISCONNECTED':
        case 'FAILED':
          title = 'Biller requires attention';
          Icon = ErrorIcon;
          error = true;
          break;
        case 'CONNECTED':
          if (txn?.ebillId) {
            title = 'Bill details are up-to-date';
            Icon = UpdatedIcon;
            updated = true;
          }
          break;
      }
    } else {
      // BPS/bill-go stuff
      showIcon = true;
      title = 'Connected';
      isPending = BPSpending || billerLoginsUtils.isRefreshing(billerLogin);
      if (isPending) {
        // pending
        title = 'Connecting';
        Icon = CircularProgress;
      } else if ((billerLogin && billerLoginsUtils.inError(billerLogin)) || !billerAccount) {
        // connection error
        title = 'Connection Error';
        Icon = ErrorIcon;
        error = true;
      } else if (txn?.ebillId) {
        title = 'Updated';
        Icon = UpdatedIcon;
        updated = true;
      }
    }
  }

  const { classes } = useStyles({ size, error, isPending, open, updated });

  return showIcon ? (
    <Tooltip title={title} arrow enterDelay={300}>
      <Icon className={classes.icon} size={size} />
    </Tooltip>
  ) : null;
}

ConnectedIcon.displayName = 'ConnectedIcon';

export default ConnectedIcon;
